import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles/styles';
import { useSwipeable } from 'react-swipeable';
import SortDrawer from './SortDrawer';
import PageHeader from './PageHeader';
import StaggeredAnimation from './common/StaggeredAnimation';
import SortButton from './common/SortButton';
import useSortState from '../hooks/useSortState';
import Identicon from './common/Identicon';

/**
 * Clean, minimal inbox list component for displaying thoughts
 */
const InboxList = ({ thoughts, emptyMessage, onDeleteThought, onNavigate }) => {
    const [sortBy, handleSortChange] = useSortState('recent');
    const [showSortDrawer, setShowSortDrawer] = useState(false);
    const navigate = useNavigate();

    // Sort thoughts based on selected criteria
    const sortedThoughts = [...thoughts].sort((a, b) => {
        if (sortBy === 'recent') {
            // Sort by timestamp (newest first)
            return new Date(b.timestamp) - new Date(a.timestamp);
        } else if (sortBy === 'importance') {
            // Sort by importance (highest first), then by timestamp for equal importance
            const importanceA = a.importance !== undefined ? a.importance : 0;
            const importanceB = b.importance !== undefined ? b.importance : 0;
            
            if (importanceB !== importanceA) {
                return importanceB - importanceA;
            } else {
                // Secondary sort by timestamp
                return new Date(b.timestamp) - new Date(a.timestamp);
            }
        }
        return 0;
    });

    // Handle navigating to thought detail with animation
    const handleNavigateToThought = (thoughtId) => {
        // Use the provided navigation handler if available
        if (onNavigate) {
            onNavigate(thoughtId);
            return;
        }
        
        // Only use direct navigation as fallback
        navigate(`/thought/${thoughtId}`);
    };

    // Empty state
    if (thoughts.length === 0) {
        return (
            <div style={{
                textAlign: "center",
                padding: "48px",
                color: colors.textSecondary,
            }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.5 }}>
                    <path d="M20 6L9 17L4 12" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px', color: colors.textPrimary }}>All clear</h3>
                <p style={{ margin: 0, maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto', color: colors.textSecondary }}>
                    {emptyMessage || "Enjoy the mental pause"}
                </p>
            </div>
        );
    }

    return (
        <div className="inbox-container smooth-scroll" style={{ width: '100%' }}>
            {/* Keep fadeIn for list items but remove fadeOut as AppContent handles that */}
            <StaggeredAnimation containerClass="inbox-list" itemSelector=".list-item"/>
            
            {/* Header with title */}
            <PageHeader 
                title={{ text: "Drafts", large: true }}
                leftContent={
                    <SortButton
                        onClick={() => setShowSortDrawer(true)}
                    />
                }
                rightContent={
                    <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: colors.textSecondary,
                        lineHeight: '38px', // Match the container height
                    }}>
                        {sortedThoughts.length} {sortedThoughts.length === 1 ? 'thought' : 'thoughts'}
                    </span>
                }
            />

            <div className="inbox-list" style={{ 
                display: 'flex', 
                flexDirection: 'column'
            }}>
                {sortedThoughts.map((thought, index) => (
                    <InboxItem 
                        key={thought.id} 
                        thought={thought} 
                        isLast={index === sortedThoughts.length - 1}
                        onDelete={() => onDeleteThought && onDeleteThought(thought.id)}
                        onNavigate={(thoughtId) => handleNavigateToThought(thoughtId)}
                    />
                ))}
            </div>

            {/* Sort drawer component */}
            <SortDrawer
                isVisible={showSortDrawer}
                onClose={() => setShowSortDrawer(false)}
                onSortChange={handleSortChange}
                currentSort={sortBy}
            />

            <style>
                {`.inbox-list {
                    padding-bottom: 120px;
                }`}
            </style>
        </div>
    );
};

/**
 * Clean, minimal inbox item with swipe functionality
 */
const InboxItem = ({ thought, isLast, onDelete, onNavigate }) => {
    const content = thought.thought || thought.content || '';
    const [swipeState, setSwipeState] = useState({ isOpen: false, direction: null });
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    
    const handleContextMenu = (e) => {
        // Allow the browser's default context menu to appear
        // Don't call e.preventDefault() or e.stopPropagation() here
        
        // Still set up our custom context menu
        setContextMenuPosition({ x: e.clientX, y: e.clientY });
        setShowContextMenu(true);
    };
    
    const handleOpenInNewTab = () => {
        const newTabUrl = `${window.location.origin}/thought/${thought.id}`;
        window.open(newTabUrl, '_blank');
        setShowContextMenu(false);
    };
    
    // Close context menu when clicking elsewhere
    useEffect(() => {
        const handleClickOutside = () => {
            setShowContextMenu(false);
        };
        
        if (showContextMenu) {
            document.addEventListener('click', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showContextMenu]);
    
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => setSwipeState({ isOpen: true, direction: 'left' }),
        onSwipedRight: () => setSwipeState({ isOpen: false, direction: 'right' }),
        preventDefaultTouchmoveEvent: true,
        trackMouse: true,
        delta: 10, // minimum swipe distance
        onSwiping: (eventData) => {
            // If horizontal movement is greater than vertical, prevent default to stop scrolling
            if (Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY)) {
                eventData.event.preventDefault();
                eventData.event.stopPropagation();
            }
        }
    });
    
    // Add a touch handler to capture touch events at the start
    const handleTouchStart = (e) => {
        // We'll use this as a backup to ensure we can capture and prevent unwanted scrolling
        const initialTouch = e.touches[0];
        const touchStartX = initialTouch.clientX;
        const touchStartY = initialTouch.clientY;
        
        const handleTouchMove = (e) => {
            if (!e.touches.length) return;
            
            const touch = e.touches[0];
            const deltaX = touch.clientX - touchStartX;
            const deltaY = touch.clientY - touchStartY;
            
            // If horizontal movement is greater than vertical, prevent default
            if (Math.abs(deltaX) > Math.abs(deltaY) * 1.2) { // 1.2 multiplier for better detection
                e.preventDefault();
            }
        };
        
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        
        const cleanup = () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', cleanup);
            document.removeEventListener('touchcancel', cleanup);
        };
        
        document.addEventListener('touchend', cleanup, { once: true });
        document.addEventListener('touchcancel', cleanup, { once: true });
    };
    
    // Check if thought has comments
    const hasComments = thought.comments && thought.comments.length > 0;
    
    return (
        <div 
            style={{ 
                position: 'relative', 
                overflow: 'hidden',
                willChange: 'transform, opacity', // Optimize for animation performance
            }}
            className="list-item"
        >
            {/* Delete button (revealed when swiped left) */}
            <div 
                onClick={(e) => {
                    e.stopPropagation(); // Prevent bubbling up to the parent click handler
                    onDelete(); // Trigger the delete action
                    setSwipeState({ isOpen: false, direction: null }); // Reset swipe state
                }}
                style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '80px',
                    backgroundColor: '#FF3B30',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer', // Add cursor pointer to indicate it's clickable
                }}
            >
                Delete
            </div>
            
            {/* Main content */}
            <a 
                {...swipeHandlers}
                onTouchStart={handleTouchStart}
                onClick={(e) => {
                    e.preventDefault(); // Prevent default link behavior
                    if (!swipeState.isOpen) {
                        onNavigate(thought.id);
                    }
                }}
                onContextMenu={handleContextMenu}
                href={`/thought/${thought.id}`}
                style={{ 
                    padding: '16px',
                    cursor: 'pointer',
                    width: '100%',
                    backgroundColor: colors.background || 'white',
                    borderBottom: isLast ? 'none' : `1px solid ${colors.divider}`,
                    transform: swipeState.isOpen ? 'translateX(-80px)' : 'translateX(0)',
                    transition: 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)', // Improved transition curve
                    touchAction: 'pan-y', // Allow vertical scrolling by default
                    display: 'flex',
                    alignItems: 'center', // Center align for better text alignment
                    textDecoration: 'none', // Remove underline from link
                    color: 'inherit', // Keep the text color
                }}
            >
                {/* Use Identicon directly with its built-in wrapper */}
                <Identicon seed={thought.id} size={36} />
                
                <div style={{
                    fontSize: '15px',
                    lineHeight: '1.5',
                    color: colors.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    whiteSpace: 'normal',
                    flex: 1,
                }}>
                    {content}
                    {hasComments && (
                        <span style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginLeft: '4px',
                            verticalAlign: 'middle',
                            position: 'relative',
                            top: '-1px'
                        }}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 6h8" stroke={colors.textSecondary} strokeWidth="1.5" strokeLinecap="round" />
                                <path d="M4 10h10" stroke={colors.textSecondary} strokeWidth="1.5" strokeLinecap="round" />
                                <path d="M4 14h6" stroke={colors.textSecondary} strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </span>
                    )}
                </div>
            </a>
            
            {/* Context Menu */}
            {showContextMenu && (
                <div
                    style={{
                        position: 'fixed',
                        top: contextMenuPosition.y,
                        left: contextMenuPosition.x,
                        backgroundColor: colors.surfaceElevated,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                        padding: '8px 0',
                        zIndex: 1000,
                        minWidth: '160px',
                    }}
                >
                    <div
                        onClick={handleOpenInNewTab}
                        style={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: colors.textPrimary,
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.hover}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        Open in new tab
                    </div>
                </div>
            )}
        </div>
    );
};

export default InboxList; 